class EWGWaterAnalysis extends HTMLElement {
  constructor() {
    super()
    this.handleReady = this.handleReady.bind(this)
    this.handleLoading = this.handleLoading.bind(this)
    this.handleUnavailable = this.handleUnavailable.bind(this)
  }

  connectedCallback() {
    this.titleEl = this.querySelector('.ewg-water-analysis__title')
    this.zipValue = this.querySelector('.ewg-water-analysis__zip-value')
    this.provider = this.querySelector('.ewg-water-analysis__provider')
    this.level = this.querySelector('.ewg-water-analysis__level')
    this.category = this.querySelector('.ewg-water-analysis__category')
    this.indicator = this.querySelector('.ewg-water-analysis__scale-indicator')
    this.description = this.querySelector('.ewg-water-analysis__description')
    this.tags = this.querySelector('.ewg-water-analysis__tags')
    this.primaryNote = this.querySelector('.ewg-water-analysis__primary-note')

    this.titleTemplate = this.titleEl?.dataset.template || this.titleEl?.textContent || ''
    this.descriptionTemplate = this.description?.dataset.template || this.description?.innerHTML || ''
    this.primaryNoteTemplate = this.primaryNote?.dataset.template || this.primaryNote?.innerHTML || ''

    document.addEventListener('wd:ewg-ready', this.handleReady)
    document.addEventListener('wd:ewg-loading', this.handleLoading)
    document.addEventListener('wd:ewg-empty', this.handleUnavailable)
    document.addEventListener('wd:ewg-error', this.handleUnavailable)

    const store = window.WDEwgReportStore
    if (!store) return

    store.init().then(state => {
      if (state?.currentOption && state?.information) {
        this.classList.remove('loading', 'empty')
        this.ariaBusy = 'false'
        this.render(state)
        return
      }
      this.handleUnavailable()
    })
  }

  disconnectedCallback() {
    document.removeEventListener('wd:ewg-ready', this.handleReady)
    document.removeEventListener('wd:ewg-loading', this.handleLoading)
    document.removeEventListener('wd:ewg-empty', this.handleUnavailable)
    document.removeEventListener('wd:ewg-error', this.handleUnavailable)
  }

  handleLoading(event) {
    const isLoading = event.detail?.isLoading !== false
    this.classList.toggle('loading', isLoading)
    this.ariaBusy = isLoading ? 'true' : 'false'
  }

  handleReady(event) {
    this.classList.remove('loading', 'empty')
    this.ariaBusy = 'false'
    this.render(event.detail)
  }

  handleUnavailable() {
    this.classList.remove('loading')
    this.ariaBusy = 'false'
    if (!new URLSearchParams(window.location.search).get('zip')?.trim()) return
    this.classList.add('empty')
    const emptyTitle = this.dataset.emptyTitle
    if (emptyTitle && this.titleEl) this.titleEl.textContent = emptyTitle
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  fillTemplate(template, vars, asHtml = false) {
    if (!template) return ''
    const get = value => (asHtml ? this.escapeHtml(value) : String(value ?? ''))
    return template
      .replace(/\{ZIP Code\}/gi, get(vars.zip))
      .replace(/\{ZIP\}/gi, get(vars.zip))
      .replace(/\{zip\}/g, get(vars.zip))
      .replace(/\{category\}/gi, get(vars.category))
      .replace(/\{highest\}/gi, get(vars.highest))
      .replace(/\{level\}/gi, get(vars.level))
      .replace(/\{top5\}/gi, get(vars.top5))
  }

  render(state) {
    if (!state) return

    const zip = String(state.zip || '').trim()
    const provider = state.currentOption?.SystemName || ''
    const information = state.information || {}
    const level = information.waterQualityLevel || ''
    const category = information.topContaminantCategory?.contaminantTypeEn || ''
    const top5 = Array.isArray(information.top5ExceedsList) ? information.top5ExceedsList : []
    const highest = information.highestExceedItem?.ContaminantName || top5[0]?.ContaminantName || ''
    const top5Names = top5
      .map(item => item?.ContaminantName)
      .filter(Boolean)
      .join(', ')
    const vars = { zip, level, category, highest, top5: top5Names }

    if (zip && this.zipValue) this.zipValue.textContent = zip
    if (provider && this.provider) this.provider.textContent = provider
    if (this.titleEl && this.titleTemplate) this.titleEl.textContent = this.fillTemplate(this.titleTemplate, vars)
    if (level && this.level) this.level.textContent = level
    if (category && this.category) this.category.textContent = category
    if (this.description && this.descriptionTemplate) {
      this.description.innerHTML = this.fillTemplate(this.descriptionTemplate, vars, true)
    }
    if (this.primaryNote && this.primaryNoteTemplate) {
      this.primaryNote.innerHTML = this.fillTemplate(this.primaryNoteTemplate, vars, true)
    }

    this.renderTags(top5)
    this.renderScale(information.totalContributionScore, level)
  }

  renderTags(list) {
    if (!this.tags) return
    const names = list.map(item => item?.ContaminantName).filter(Boolean)
    if (!names.length) return

    this.tags.replaceChildren()
    names.slice(0, 5).forEach((name, index) => {
      const tag = document.createElement('span')
      tag.className = `ewg-water-analysis__tag${index === 0 ? ' ewg-water-analysis__tag--highlight' : ''}`
      tag.textContent = name
      this.tags.appendChild(tag)
    })
  }

  renderScale(score, level) {
    if (!this.indicator) return

    const LEVEL_POSITION = {
      'Low Concern': 8,
      'Moderate Concern': 28,
      'Elevated Concern': 48,
      'High Concern': 62,
      'Very High Concern': 78,
      'Severe Concern': 93
    }

    const numericScore = Number(score)
    const percent = Number.isFinite(numericScore)
      ? Math.min(Math.max(numericScore, 2), 98)
      : LEVEL_POSITION[level] || 50

    this.indicator.style.left = `${percent}%`
  }
}

if (!customElements.get('ewg-water-analysis')) {
  customElements.define('ewg-water-analysis', EWGWaterAnalysis)
}
